import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  GraduationCap, Plus, Search, Mail, Phone, Eye, Edit2, Trash2, 
  RefreshCw, BookOpen, Users, Send, Folder, Upload, Download, File, Clock, X
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveUploadUrl } from '../utils/media';

// Keep only digits and a single leading '+', capped at 15 digits (E.164 max) — for phone inputs.
const sanitizePhone = (value) => {
  if (!value) return '';
  let cleaned = value.replace(/[^\d+]/g, '');
  const hasPlus = cleaned.startsWith('+');
  const digits = cleaned.replace(/\+/g, '').slice(0, 15);
  return (hasPlus ? '+' : '') + digits;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());

// Allow only letters, spaces, apostrophes and hyphens in name fields (no digits / special chars).
const sanitizeName = (value) => {
  if (!value) return '';
  try {
    return value.replace(/[^\p{L}\s'-]/gu, '');
  } catch {
    return value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s'-]/g, '');
  }
};

/** Normalize FastAPI/axios errors into a toast-safe string (never render raw objects). */
const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean)
      .join(', ') || fallback;
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const clearDialogBodyLocks = () => {
  // Radix Dialog can leave body scroll/pointer locks if unmounted mid-close
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  document.body.removeAttribute('data-scroll-locked');
};

const TrainersPage = ({ onMenuClick }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Documents state
  const [detailTab, setDetailTab] = useState('info');
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentNotes, setDocumentNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Reference data
  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    specialization: '',
    years_of_experience: '',
    bio: ''
  });
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const fetchTrainers = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const response = await api.get('/trainers');
      setTrainers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast.error('Erreur lors du chargement des formateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [groupsRes, coursesRes] = await Promise.all([
        api.get('/groups').catch(() => ({ data: [] })),
        api.get('/content/courses').catch(() => api.get('/courses')).catch(() => ({ data: [] })),
      ]);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      setCourses(Array.isArray(coursesRes.data) ? coursesRes.data : []);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchTrainers();
      fetchReferenceData();
    }
  }, [user, fetchTrainers, fetchReferenceData]);

  // Auto-open create modal if ?action=create in URL
  useEffect(() => {
    if (searchParams.get('action') === 'create' && user?.role === 'cfa_admin') {
      handleOpenCreate();
      // Replace (not push) so the Back button returns to the dashboard, not this modal.
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, user]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      gender: '',
      specialization: '',
      years_of_experience: '',
      bio: ''
    });
    setSelectedCourseId('');
  };

  // Document types for trainers
  const documentTypes = [
    { value: 'contrat', label: 'Contrat formateur' },
    { value: 'rib', label: 'RIB' },
    { value: 'piece_identite', label: "Pièce d'identité" },
    { value: 'cv', label: 'CV' },
    { value: 'certification', label: 'Certification' },
    { value: 'diplome', label: 'Diplôme' },
    { value: 'attestation_assurance', label: "Attestation d'assurance" },
    { value: 'kbis', label: 'Extrait Kbis' },
    { value: 'autre', label: 'Autre document' }
  ];

  const fetchTrainerDocuments = async (trainerId) => {
    setLoadingDocuments(true);
    try {
      const response = await api.get(`/trainer-docs/${trainerId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile || !documentType) {
      toast.error('Veuillez sélectionner un fichier et un type de document');
      return;
    }
    
    setUploadingDocument(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', selectedFile);
    formDataUpload.append('document_type', documentType);
    if (documentNotes) {
      formDataUpload.append('notes', documentNotes);
    }
    
    try {
      await api.post(`/trainer-docs/${selectedTrainer.id}/documents`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploadé avec succès');
      setSelectedFile(null);
      setDocumentType('');
      setDocumentNotes('');
      fetchTrainerDocuments(selectedTrainer.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'upload du document'));
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.get(`/trainer-docs/${selectedTrainer.id}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handlePreviewDocument = async (doc) => {
    try {
      const response = await api.get(`/trainer-docs/${selectedTrainer.id}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: doc.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke later to let the new tab load the resource
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Erreur lors de l\'aperçu du document');
    }
  };

  const handleSendLoginEmail = async (trainer) => {
    try {
      await api.post(`/trainers/${trainer.id}/resend-activation`);
      toast.success(`Email de connexion envoyé à ${trainer.email}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'envoi de l\'email'));
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      await api.delete(`/trainer-docs/${selectedTrainer.id}/documents/${docId}`);
      toast.success('Document supprimé');
      fetchTrainerDocuments(selectedTrainer.id);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleOpenEdit = (trainer) => {
    setSelectedTrainer(trainer);
    setFormData({
      first_name: trainer.first_name || '',
      last_name: trainer.last_name || '',
      email: trainer.email || '',
      phone: trainer.phone || '',
      gender: trainer.gender || '',
      specialization: trainer.specialization || '',
      years_of_experience: trainer.years_of_experience ?? '',
      bio: trainer.bio || ''
    });
    setShowEditModal(true);
  };

  const handleOpenDetail = (trainer) => {
    setSelectedTrainer(trainer);
    setDetailTab('info');
    setDocuments([]);
    setSelectedFile(null);
    setDocumentType('');
    setDocumentNotes('');
    setShowDetailModal(true);
    fetchTrainerDocuments(trainer.id);
  };

  const validateTrainerForm = () => {
    if (!formData.first_name?.trim() || !formData.last_name?.trim() || !formData.email?.trim()
        || !formData.phone?.trim() || !formData.gender) {
      toast.error('Veuillez remplir tous les champs obligatoires (Nom, Prénom, Email, Téléphone, Sexe).');
      return false;
    }
    if (!isValidEmail(formData.email)) {
      toast.error('Veuillez saisir une adresse email valide (ex. nom@domaine.com).');
      return false;
    }
    if (formData.phone.replace(/\D/g, '').length < 8) {
      toast.error('Veuillez saisir un numéro de téléphone valide.');
      return false;
    }
    return true;
  };

  const handleCreateTrainer = async () => {
    if (!validateTrainerForm()) return;

    setSaving(true);
    try {
      const tempPassword = Math.random().toString(36).slice(-12);
      
      const res = await api.post('/trainers', {
        ...formData,
        years_of_experience: formData.years_of_experience === '' ? null : (parseInt(formData.years_of_experience, 10) || null),
        password: tempPassword
      });
      const created = res.data;

      if (selectedCourseId && created?.id) {
        try {
          // Correct route is /courses/{id}/trainers (not under /content)
          await api.post(`/courses/${selectedCourseId}/trainers`, {
            trainer_ids: [created.id]
          });
        } catch (courseErr) {
          console.error('Course assignment failed:', courseErr);
          toast.warning('Formateur créé, mais l\'assignation au cours a échoué.');
        }
      }

      // Show the new trainer immediately (avoid blank list while waiting for refetch)
      if (created?.id) {
        setTrainers((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          if (list.some((t) => t.id === created.id)) return list;
          return [created, ...list];
        });
      }

      // Close dialog before reset/refetch — prevents stuck Radix overlay / white screen
      setShowCreateModal(false);
      setSaving(false);
      toast.success('Formateur créé avec succès. Un email d\'activation a été envoyé.');

      window.setTimeout(() => {
        resetForm();
        clearDialogBodyLocks();
        fetchTrainers();
      }, 150);
    } catch (error) {
      console.error('Error creating trainer:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création du formateur'));
      setSaving(false);
    }
  };

  const handleUpdateTrainer = async () => {
    if (!validateTrainerForm()) return;

    setSaving(true);
    try {
      await api.put(`/trainers/${selectedTrainer.id}`, {
        ...formData,
        years_of_experience: formData.years_of_experience === '' ? null : (parseInt(formData.years_of_experience, 10) || null),
      });
      toast.success('Formateur mis à jour avec succès');
      setShowEditModal(false);
      clearDialogBodyLocks();
      fetchTrainers();
    } catch (error) {
      console.error('Error updating trainer:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la mise à jour'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTrainer = async (trainerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce formateur ? Cette action est irréversible.')) {
      return;
    }

    try {
      await api.delete(`/trainers/${trainerId}`);
      toast.success('Formateur supprimé');
      fetchTrainers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getTrainerName = (trainer) => {
    return `${trainer.first_name || ''} ${trainer.last_name || ''}`.trim();
  };

  const getTrainerInitials = (trainer) => {
    const first = trainer.first_name?.[0] || '';
    const last = trainer.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase();
  };

  // Get avatar URL (handles both relative and absolute URLs)
  const getAvatarUrl = (trainer) => resolveUploadUrl(trainer?.avatar_url);

  // Get trainer's assigned groups
  const getTrainerGroups = (trainerId) => {
    if (!Array.isArray(groups)) return [];
    return groups.filter(g => g.trainer_id === trainerId);
  };

  const trainersList = Array.isArray(trainers) ? trainers : [];
  const filteredTrainers = trainersList.filter((trainer) => {
    const name = getTrainerName(trainer).toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || 
           trainer.email?.toLowerCase().includes(search) ||
           trainer.specialization?.toLowerCase().includes(search);
  });

  const renderTrainerForm = (isCreate = false) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="last_name">Nom *</Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: sanitizeName(e.target.value)})}
            placeholder="Dupont"
            data-testid="trainer-last-name"
          />
        </div>
        
        <div>
          <Label htmlFor="first_name">Prénom *</Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: sanitizeName(e.target.value)})}
            placeholder="Jean"
            data-testid="trainer-first-name"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="jean.dupont@email.com"
            data-testid="trainer-email"
          />
        </div>
        
        <div>
          <Label htmlFor="phone">Téléphone *</Label>
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            maxLength={16}
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: sanitizePhone(e.target.value)})}
            placeholder="0612345678"
            data-testid="trainer-phone"
          />
          <p className="text-xs text-slate-500 mt-1">Chiffres et « + » uniquement, 15 chiffres maximum.</p>
        </div>
      </div>

      <div>
        <Label htmlFor="gender">Sexe *</Label>
        <select
          id="gender"
          value={formData.gender}
          onChange={(e) => setFormData({...formData, gender: e.target.value})}
          className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-md px-3 py-2 text-sm focus:ring-emerald-500 focus:border-emerald-500"
          data-testid="trainer-gender"
        >
          <option value="" disabled className="text-slate-900 dark:text-white dark:bg-slate-800">Sélectionner…</option>
          <option value="M" className="text-slate-900 dark:text-white dark:bg-slate-800">Homme</option>
          <option value="F" className="text-slate-900 dark:text-white dark:bg-slate-800">Femme</option>
          <option value="Autre" className="text-slate-900 dark:text-white dark:bg-slate-800">Autre</option>
        </select>
      </div>
      
      <div>
        <Label htmlFor="specialization">Spécialité</Label>
        <Input
          id="specialization"
          value={formData.specialization}
          onChange={(e) => setFormData({...formData, specialization: e.target.value})}
          placeholder="ex: Développement Web, Management, Marketing..."
          data-testid="trainer-specialization"
        />
      </div>

      <div>
        <Label htmlFor="years_of_experience">Années d'expérience</Label>
        <Input
          id="years_of_experience"
          type="number"
          min="0"
          max="60"
          inputMode="numeric"
          value={formData.years_of_experience}
          onChange={(e) => setFormData({...formData, years_of_experience: e.target.value.replace(/\D/g, '').slice(0, 2)})}
          placeholder="ex: 8"
          data-testid="trainer-experience"
        />
      </div>
      
      <div>
        <Label htmlFor="bio">Biographie</Label>
        <Textarea
          id="bio"
          value={formData.bio}
          onChange={(e) => setFormData({...formData, bio: e.target.value})}
          placeholder="Décrivez l'expérience et les compétences du formateur..."
          rows={3}
          data-testid="trainer-bio"
        />
      </div>

      {isCreate && Array.isArray(courses) && courses.length > 0 && (
        <div>
          <Label htmlFor="course_id">Assigner un cours</Label>
          <Select
            value={selectedCourseId || undefined}
            onValueChange={setSelectedCourseId}
          >
            <SelectTrigger data-testid="trainer-course-select">
              <SelectValue placeholder="Aucun cours (optionnel)" />
            </SelectTrigger>
            <SelectContent>
              {courses.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header title="Gestion des Formateurs" onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Gestion des Formateurs" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Liste des Formateurs</h2>
            <p className="text-slate-600 dark:text-slate-400">{trainersList.length} formateur(s) au total</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => { setSearchTerm(''); fetchTrainers({ showLoading: true }); }}
              data-testid="refresh-trainers-btn"
              title="Actualiser"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={handleOpenCreate} data-testid="add-trainer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Créer un formateur
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un formateur par nom, email ou spécialité..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-trainers-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Trainers Grid */}
        {filteredTrainers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTrainers.map((trainer) => {
              const trainerGroups = getTrainerGroups(trainer.id);
              
              return (
                <Card key={trainer.id} className="hover:shadow-lg transition-shadow" data-testid={`trainer-card-${trainer.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center flex-shrink-0">
                          {trainer.avatar_url ? (
                            <img 
                              src={getAvatarUrl(trainer)} 
                              alt={getTrainerName(trainer)}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-semibold text-lg">
                              {getTrainerInitials(trainer)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                            {getTrainerName(trainer)}
                          </h3>
                          {trainer.specialization && (
                            <Badge variant="outline" translate="no" className="notranslate mt-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                              {trainer.specialization}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{trainer.email}</span>
                      </div>
                      {trainer.phone && (
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Phone className="w-4 h-4" />
                          <span>{trainer.phone}</span>
                        </div>
                      )}
                      {trainerGroups.length > 0 && (
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Users className="w-4 h-4" />
                          <span>{trainerGroups.length} groupe(s) assigné(s)</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleOpenDetail(trainer)}
                        data-testid={`view-trainer-${trainer.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenEdit(trainer)}
                        data-testid={`edit-trainer-${trainer.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteTrainer(trainer.id)}
                        data-testid={`delete-trainer-${trainer.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {searchTerm ? 'Aucun résultat' : 'Aucun formateur'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {searchTerm 
                  ? 'Modifiez votre recherche pour trouver des formateurs' 
                  : 'Commencez par créer votre premier formateur'}
              </p>
              {!searchTerm && (
                <Button onClick={handleOpenCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un formateur
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Modal */}
      <Dialog
        open={showCreateModal}
        onOpenChange={(open) => {
          setShowCreateModal(open);
          if (!open) {
            resetForm();
            clearDialogBodyLocks();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-emerald-600" />
              Nouveau formateur
            </DialogTitle>
            <DialogDescription>
              Créez un nouveau formateur. Un email d'activation sera envoyé automatiquement.
            </DialogDescription>
          </DialogHeader>
          
          {renderTrainerForm(true)}
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
                clearDialogBodyLocks();
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleCreateTrainer} disabled={saving} data-testid="submit-create-trainer">
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer le formateur
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-emerald-600" />
              Modifier le formateur
            </DialogTitle>
            <DialogDescription>
              Modifiez les informations de {selectedTrainer?.first_name} {selectedTrainer?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          {renderTrainerForm()}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateTrainer} disabled={saving} data-testid="submit-edit-trainer">
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-emerald-600" />
              Fiche formateur
            </DialogTitle>
          </DialogHeader>
          
          {selectedTrainer && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Header */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center flex-shrink-0">
                  {selectedTrainer.avatar_url ? (
                    <img 
                      src={getAvatarUrl(selectedTrainer)} 
                      alt={getTrainerName(selectedTrainer)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-bold text-xl">
                      {getTrainerInitials(selectedTrainer)}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                    {getTrainerName(selectedTrainer)}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {selectedTrainer.specialization && (
                      <Badge variant="outline" translate="no" className="notranslate bg-emerald-50 text-emerald-700 border-emerald-200">
                        {selectedTrainer.specialization}
                      </Badge>
                    )}
                    {(selectedTrainer.years_of_experience || selectedTrainer.years_of_experience === 0) && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {selectedTrainer.years_of_experience} an{selectedTrainer.years_of_experience > 1 ? 's' : ''} d'expérience
                      </Badge>
                    )}
                    {selectedTrainer.gender && (
                      <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                        {selectedTrainer.gender === 'M' ? 'Homme' : selectedTrainer.gender === 'F' ? 'Femme' : selectedTrainer.gender}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-slate-200 flex mb-4">
                <button
                  onClick={() => setDetailTab('info')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    detailTab === 'info'
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Informations
                </button>
                <button
                  onClick={() => setDetailTab('documents')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center ${
                    detailTab === 'documents'
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Folder className="w-4 h-4 mr-2" />
                  Documents
                  {documents.length > 0 && (
                    <span className="ml-2 bg-slate-200 text-slate-700 text-xs px-1.5 py-0.5 rounded">{documents.length}</span>
                  )}
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {detailTab === 'info' && (
                  <div className="space-y-6">
                    {/* Quick actions */}
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendLoginEmail(selectedTrainer)}
                        className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer un email de connexion
                      </Button>
                    </div>

                    {/* Contact */}
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white mb-2">Contact</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4" />
                          {selectedTrainer.email}
                        </div>
                        {selectedTrainer.phone && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-4 h-4" />
                            {selectedTrainer.phone}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bio */}
                    {selectedTrainer.bio && (
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white mb-2">Biographie</h4>
                        <p className="text-sm text-slate-600">{selectedTrainer.bio}</p>
                      </div>
                    )}

                    {/* Assigned Groups */}
                    {(() => {
                      const trainerGroups = getTrainerGroups(selectedTrainer.id);
                      if (trainerGroups.length > 0) {
                        return (
                          <div>
                            <h4 className="font-medium text-slate-900 dark:text-white mb-2">Groupes assignés</h4>
                            <div className="space-y-2">
                              {trainerGroups.map(group => (
                                <div key={group.id} className="flex items-center gap-2 text-sm text-slate-600">
                                  <Users className="w-4 h-4" />
                                  {group.name}
                                  {group.course_name && (
                                    <span className="text-slate-400">({group.course_name})</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {detailTab === 'documents' && (
                  <div className="space-y-6">
                    {/* Upload Form */}
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center">
                          <Upload className="w-4 h-4 mr-2" />
                          Ajouter un document
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Type de document *</label>
                            <select
                              value={documentType}
                              onChange={(e) => setDocumentType(e.target.value)}
                              className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-md px-3 py-2 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                            >
                              <option value="" disabled className="text-slate-900 dark:text-white dark:bg-slate-800">Sélectionner un type</option>
                              {documentTypes.map(type => (
                                <option key={type.value} value={type.value} className="text-slate-900 dark:text-white dark:bg-slate-800">{type.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Fichier *</label>
                            <Input
                              type="file"
                              onChange={(e) => setSelectedFile(e.target.files[0])}
                              className="text-sm"
                            />
                            {selectedFile && (
                              <p className="text-xs text-slate-500 mt-1">
                                {selectedFile.name} ({formatFileSize(selectedFile.size)})
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optionnel)</label>
                            <Input
                              value={documentNotes}
                              onChange={(e) => setDocumentNotes(e.target.value)}
                              placeholder="Remarques sur ce document..."
                            />
                          </div>
                          <Button 
                            onClick={handleUploadDocument} 
                            disabled={uploadingDocument || !selectedFile || !documentType}
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                          >
                            {uploadingDocument ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Upload en cours...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Uploader le document
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Documents List */}
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white mb-3">
                        Documents du dossier ({documents.length})
                      </h4>
                      {loadingDocuments ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
                        </div>
                      ) : documents.length > 0 ? (
                        <div className="space-y-2">
                          {documents.map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <File className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                    {doc.original_filename}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {doc.document_type_label} • {formatFileSize(doc.file_size)}
                                  </p>
                                  <p className="text-xs text-slate-400 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : '—'}
                                    {doc.uploaded_by && ` par ${doc.uploaded_by}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handlePreviewDocument(doc)}
                                  title="Aperçu"
                                >
                                  <Eye className="w-4 h-4 text-slate-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadDocument(doc)}
                                  title="Télécharger"
                                >
                                  <Download className="w-4 h-4 text-slate-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  title="Supprimer"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <Folder className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>Aucun document dans le dossier</p>
                          <p className="text-sm">Utilisez le formulaire ci-dessus pour ajouter des documents</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainersPage;
